/**
 * Hook for periodic LOC status polling
 * P2 task #4.12: Add LOC status polling (expired/redeemed)
 * 
 * Polls LOC status every 30 seconds to detect:
 * - Expired LOCs (expirationTimestamp <= now)
 * - Redeemed LOCs (via events or contract state)
 */

import { useEffect, useRef } from 'react'
import { useLOCDetails } from './useLOC'
import { LOCEvent } from './useLOCEvents'

export interface LOCStatusUpdate {
  id: bigint
  previousStatus: 'active' | 'expired' | 'redeemed'
  currentStatus: 'active' | 'expired' | 'redeemed'
}

const POLL_INTERVAL_MS = 30 * 1000 // 30 seconds

/**
 * Determines LOC status based on expiry timestamp
 */
function getLOCStatus(expirationTimestamp: number): 'active' | 'expired' {
  const now = Math.floor(Date.now() / 1000)
  return expirationTimestamp > now ? 'active' : 'expired'
}

/**
 * Hook to poll a single LOC's status
 */
export function useLOCStatusPolling(
  locId: bigint | undefined,
  onStatusChange?: (update: LOCStatusUpdate) => void
) {
  const { loc, refetch } = useLOCDetails(locId)

  useEffect(() => {
    if (!locId || !loc) return

    const previousStatus = getLOCStatus(Number(loc.expiry))

    const interval = setInterval(async () => {
      // Refetch LOC details
      const result = await refetch()
      if (!result.data) return

      const updatedLoc = result.data as any
      const currentStatus = getLOCStatus(Number(updatedLoc.expiry))

      // Notify if status changed
      if (currentStatus !== previousStatus && onStatusChange) {
        onStatusChange({
          id: locId,
          previousStatus,
          currentStatus,
        })
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [locId, loc, refetch, onStatusChange])
}

/**
 * Hook to poll multiple LOCs' statuses
 */
export function useBulkLOCStatusPolling(
  locs: LOCEvent[],
  onStatusChange?: (updates: LOCStatusUpdate[]) => void
) {
  const statusMapRef = useRef<Map<string, 'active' | 'expired'>>(new Map())

  useEffect(() => {
    if (locs.length === 0) return

    // Initialize status map on first run
    const map = statusMapRef.current
    locs.forEach(loc => {
      const key = loc.id.toString()
      if (!map.has(key)) {
        map.set(key, getLOCStatus(loc.expirationTimestamp))
      }
    })

    const interval = setInterval(() => {
      const updates: LOCStatusUpdate[] = []

      locs.forEach(loc => {
        const key = loc.id.toString()
        const previousStatus = map.get(key) || 'active'
        const currentStatus = getLOCStatus(loc.expirationTimestamp)

        if (currentStatus !== previousStatus) {
          map.set(key, currentStatus)
          updates.push({
            id: loc.id,
            previousStatus,
            currentStatus,
          })
        }
      })

      if (updates.length > 0 && onStatusChange) {
        onStatusChange(updates)
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [locs, onStatusChange])
}

/**
 * Hook to track LOCs nearing expiration (<24h remaining)
 */
export function useExpiringLOCsAlert(
  locs: LOCEvent[],
  onAlert?: (expiringLOCs: LOCEvent[]) => void
) {
  useEffect(() => {
    if (locs.length === 0) return

    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000)
      const twentyFourHours = 24 * 60 * 60

      const expiringLOCs = locs.filter(loc => {
        const timeUntilExpiry = loc.expirationTimestamp - now
        return timeUntilExpiry > 0 && timeUntilExpiry < twentyFourHours
      })

      if (expiringLOCs.length > 0 && onAlert) {
        onAlert(expiringLOCs)
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [locs, onAlert])
}
